import { useState } from 'react';
import { Button } from '@plerionhq/design-system';
import { AnchoredPopover } from '../../component-library/anchored-popover';
import { ReviewDatePresets } from './review-date-presets';
import { toDateInputValue } from '../../lib/review-date';

import { useTranslation } from 'react-i18next';

const dateFieldClass =
  'w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-action-primary focus:ring-1 focus:ring-action-primary focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';

type ReviewDatePopoverProperties = {
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  title: string;
  description: string;
  // The date the grant currently carries, as a stored ISO string.
  currentDate: string | null;
  // Both omitted where the date is just a reminder. Only a trust window is
  // bounded: future-only, and no further out than the API's horizon.
  min?: string;
  max?: string;
  isSaving: boolean;
  // Fixed text. Naming the chosen date here would grow the button past the
  // popover as the date changes.
  confirmLabel: string;
  // Omitted where there is no "no date" state to fall back to.
  onClear?: () => void;
  warning?: string;
  // The chosen date as a 'YYYY-MM-DD' value.
  onConfirm: (reviewDate: string) => void;
};

/**
 * Picks a review date, with one-click presets and a calendar.
 *
 * Shared by every place a review date is chosen so the shortcuts and the bounds
 * cannot drift apart. Rendered only while open, so the field is seeded on mount
 * and a cancelled edit is discarded rather than lingering until the next open.
 */
export function ReviewDatePopover({
  onClose,
  anchorRef,
  title,
  description,
  currentDate,
  min,
  max,
  isSaving,
  confirmLabel,
  onClear,
  warning,
  onConfirm,
}: ReviewDatePopoverProperties) {
  const { t } = useTranslation('accessGrants');
  const inBounds = (date: string) =>
    date !== '' &&
    (min === undefined || date >= min) &&
    (max === undefined || date <= max);

  const [reviewDate, setReviewDate] = useState(() => {
    // Seeding from the grant only helps if the date is one that would still be
    // accepted — an overdue trust must not re-save its own expired date.
    const current = toDateInputValue(currentDate);
    return inBounds(current) ? current : '';
  });

  const isValid = inBounds(reviewDate);

  return (
    <AnchoredPopover onClose={onClose} anchorRef={anchorRef} label={title}>
      <div className="flex w-72 flex-col gap-3">
        <div>
          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
            {title}
          </p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {description}
          </p>
        </div>

        {warning && (
          <p className="text-severity-medium-base text-xs">{warning}</p>
        )}

        <ReviewDatePresets value={reviewDate} onChange={setReviewDate} />

        <label className="block">
          <span className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
            {t('reviewDate')}
          </span>
          <input
            type="date"
            className={dateFieldClass}
            value={reviewDate}
            min={min}
            max={max}
            onChange={(event) => setReviewDate(event.target.value)}
          />
        </label>

        <div className="flex items-center justify-end gap-2">
          {onClear && (
            <Button
              variant="secondary"
              size="small"
              label={t('clear')}
              onClick={onClear}
              // Sits left of Cancel/Save: it is a save, not a dismissal.
              className="mr-auto"
            />
          )}
          <Button
            variant="secondary"
            size="small"
            label={t('common:cancel')}
            onClick={onClose}
          />
          <Button
            variant="primary"
            size="small"
            label={confirmLabel}
            isDisabled={!isValid}
            isLoading={isSaving}
            onClick={() => onConfirm(reviewDate)}
          />
        </div>
      </div>
    </AnchoredPopover>
  );
}
